// Recovery, and the hand-back that closes the case.
//
// THE SCORE decides when the patient may leave: Modified Aldrete, five
// parameters each 0-2, out of 10, dischargeable at 9. An incomplete score shows
// as incomplete rather than as a low total — a patient with three parameters
// recorded has not been assessed, and a number there would look like a judgement
// nobody made.
//
// Check-out routes the patient back to the held ward bed or on to ICU through
// the existing bed machinery, and closes the case. A bed problem is reported as
// a warning, never as a failure: the patient physically left recovery, and
// refusing to record that would leave the board lying about where they are.

import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import {
  IPD_COLORS,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTextField,
} from "../../../ipd/utils";
import { formatIstDateTime } from "../../../../utils/istDateTime";
import {
  addRecoveryOrder,
  fetchRecovery,
  markReadyForTransfer,
  receiveInRecovery,
  recordAldrete,
  recoveryCheckout,
} from "../../api/otApi";
import { Field, FieldGrid, OtSelect, errorMessage, report } from "../common";
import { VitalsDialog } from "./FlowPanel";
import { ALDRETE_DISCHARGEABLE_AT, ALDRETE_PARAMS, RECOVERY_DESTINATIONS } from "../../constants";

/**
 * The pain score, beside the number that decides discharge.
 *
 * The Modified Aldrete has five parameters and none of them is pain, so a
 * patient can score 10 of 10 and be in agony. The score was already being
 * recorded -- it was the last item of a run-on observation line -- and nothing
 * ever put it where the discharge decision is made.
 *
 * THE TREND IS THE POINT. "6 then 8 then 9" and "9 then 8 then 6" are the same
 * latest number and opposite clinical pictures, so the run is shown, oldest
 * first, and the direction is stated in words rather than left to be read off
 * the digits.
 */
function PainCard({ pain }) {
  if (!pain || pain.latest === null || pain.latest === undefined) {
    return (
      <Box
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: 1,
          border: `1px dashed ${IPD_COLORS.border}`,
          fontSize: 13,
          color: IPD_COLORS.textSecondary,
        }}
      >
        No pain score recorded in recovery. Aldrete does not measure pain — record
        one with the observations.
      </Box>
    );
  }

  const trend = pain.trend || [];
  const first = trend.length > 1 ? trend[0].score : null;
  const direction =
    first === null
      ? null
      : pain.latest > first
      ? "worsening"
      : pain.latest < first
      ? "settling"
      : "unchanged";

  return (
    <Box
      sx={{
        mt: 2.5,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: pain.high ? IPD_COLORS.warning.bg : IPD_COLORS.success.bg,
        color: pain.high ? IPD_COLORS.warning.text : IPD_COLORS.success.text,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
        Pain {pain.latest} / {pain.max}
        {pain.high ? ` — at or above ${pain.warn_at}` : ""}
      </Typography>
      <Typography sx={{ fontSize: 12.5, mt: 0.25 }}>
        {pain.high
          ? "Severe. Check-out is not blocked, but the receiving nurse has to be told."
          : "Within the range recovery treats."}
      </Typography>

      {trend.length > 1 && (
        <Box sx={{ mt: 1, display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
          {trend.map((t, i) => (
            <Box
              key={`${t.at}-${i}`}
              title={`${formatIstDateTime(t.at)}${t.by ? ` — ${t.by}` : ""}`}
              sx={{
                px: 0.9,
                py: 0.2,
                borderRadius: 0.75,
                fontSize: 12,
                fontWeight: i === trend.length - 1 ? 800 : 600,
                border: `1px solid currentColor`,
                opacity: i === trend.length - 1 ? 1 : 0.6,
              }}
            >
              {t.score}
            </Box>
          ))}
          {direction && (
            <Typography sx={{ fontSize: 12, ml: 0.5 }}>{direction}</Typography>
          )}
        </Box>
      )}

      {pain.latest_at && (
        <Typography sx={{ fontSize: 11.5, mt: 0.75, opacity: 0.85 }}>
          Last recorded {formatIstDateTime(pain.latest_at)}
        </Typography>
      )}
    </Box>
  );
}

export default function RecoveryPanel({ otCase, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [vitalsOpen, setVitalsOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [checkout, setCheckout] = useState({ destination: "WARD", destination_bed_code: "" });

  const load = useCallback(async () => {
    setError("");
    try {
      setData(await fetchRecovery(otCase.id));
    } catch (err) {
      setError(errorMessage(err, "Could not load the recovery record"));
    }
  }, [otCase.id]);

  useEffect(() => {
    load();
  }, [load]);

  // The case moves when the patient is received into recovery, marked ready,
  // or checked out. Scoring them does not move it, so scoring re-reads this
  // one payload and leaves the parent alone — five parameters used to mean
  // five case reloads.
  const refreshAll = async () => {
    await load();
    onChanged();
  };

  if (error) return <Typography sx={{ color: IPD_COLORS.error.text }}>{error}</Typography>;

  const recovery = data?.recovery;

  if (!recovery) {
    return (
      <IpdSectionCard title="Recovery">
        <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
          {otCase.lifecycle_status === "IN_RECOVERY"
            ? "The patient has left theatre. Receive them into the recovery bay to start the record."
            : "The patient has not left theatre yet."}
        </Typography>
        <IpdButton
          disabled={otCase.lifecycle_status !== "IN_RECOVERY"}
          onClick={async () => {
            if (report(await receiveInRecovery(otCase.id, {}))) refreshAll();
          }}
        >
          Receive the patient in recovery
        </IpdButton>
      </IpdSectionCard>
    );
  }

  const total = recovery.aldrete_total;

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <IpdSectionCard title="Recovery record">
        <FieldGrid columns={4}>
          <Field label="Arrived" value={formatIstDateTime(recovery.arrived_at)} />
          <Field label="Handover from" value={recovery.handover_from} />
          <Field label="Anaesthesia state" value={recovery.anaesthesia_state} />
          <Field label="Departed" value={formatIstDateTime(recovery.departed_at)} />
          <Field label="Handover notes" value={recovery.handover_notes} span={4} />
        </FieldGrid>
      </IpdSectionCard>

      <IpdSectionCard
        title="Modified Aldrete score"
        subtitle={`Five parameters, 0 to 2 each. Dischargeable from ${ALDRETE_DISCHARGEABLE_AT} of 10.`}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(5, 1fr)" },
            gap: 2,
          }}
        >
          {ALDRETE_PARAMS.map((p) => (
            <IpdFormField key={p.key} label={p.label}>
              <OtSelect
                placeholder="—"
                // The five parameters are nested under `aldrete`, not flat on
                // the row. Read flat they were always undefined, so a scored
                // patient's selects came up blank and the score looked lost.
                value={recovery.aldrete?.[p.key] ?? ""}
                onChange={async (e) => {
                  const res = await recordAldrete(otCase.id, { [p.key]: e.target.value });
                  // The score's own message is worth reading — it says whether
                  // the patient has become dischargeable — so this one is not
                  // quiet.
                  if (report(res)) setData((c) => ({ ...c, recovery: res.data }));
                }}
                options={[
                  { value: 0, label: "0" },
                  { value: 1, label: "1" },
                  { value: 2, label: "2" },
                ]}
              />
            </IpdFormField>
          ))}

          <Box
            sx={{
              mt: 2.5,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: recovery.dischargeable ? IPD_COLORS.success.bg : IPD_COLORS.warning.bg,
              color: recovery.dischargeable ? IPD_COLORS.success.text : IPD_COLORS.warning.text,
              fontWeight: 700,
              fontSize: 13.5,
            }}
          >
            {total === null || total === undefined
              ? "Not all five parameters are recorded yet — the patient has not been fully assessed."
              : recovery.dischargeable
              ? `${total} / 10 — the patient may leave recovery.`
              : `${total} / 10 — below ${ALDRETE_DISCHARGEABLE_AT}, keep the patient in recovery.`}
          </Box>
          {recovery.scored_at && (
            <Typography sx={{ mt: 1, fontSize: 12, color: IPD_COLORS.textSecondary }}>
              Last scored {formatIstDateTime(recovery.scored_at)} by {recovery.scored_by_name || "—"}
            </Typography>
          )}

          <PainCard pain={recovery.pain} />
        </Box>
      </IpdSectionCard>

      <IpdSectionCard
        title="Recovery observations"
        actions={
          <Button size="small" variant="contained" onClick={() => setVitalsOpen(true)}>
            Record observations
          </Button>
        }
      >
        {(data.vitals || []).length === 0 ? (
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
            Nothing recorded yet.
          </Typography>
        ) : (
          data.vitals.map((v) => (
            <Typography
              key={v.id}
              sx={{ fontSize: 13, mb: 0.5, borderBottom: `1px solid ${IPD_COLORS.border}`, pb: 0.5 }}
            >
              {formatIstDateTime(v.recorded_at)} — BP {v.systolic_bp ?? "—"}/{v.diastolic_bp ?? "—"},
              HR {v.heart_rate ?? "—"}, SpO₂ {v.spo2 ?? "—"}%, pain {v.pain_score ?? "—"}
            </Typography>
          ))
        )}
      </IpdSectionCard>

      <IpdSectionCard
        title="Post-operative instructions for the ward"
        subtitle="Free text. Orders that route somewhere go through the theatre order screen."
      >
        {(data.orders || []).map((o) => (
          <Typography key={o.id} sx={{ fontSize: 13, py: 0.4 }}>
            • {o.instruction}
            {o.category ? ` (${o.category})` : ""} —{" "}
            <span style={{ color: IPD_COLORS.textSecondary }}>
              {o.recorded_by_name} {formatIstDateTime(o.recorded_at)}
            </span>
          </Typography>
        ))}
        <Box sx={{ mt: 2, display: "flex", gap: 1.5, alignItems: "flex-end" }}>
          <Box sx={{ flex: 1 }}>
            <IpdFormField label="Add an instruction">
              <IpdTextField
                fullWidth
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
              />
            </IpdFormField>
          </Box>
          <IpdButton
            disabled={!instruction.trim()}
            onClick={async () => {
              if (report(await addRecoveryOrder(otCase.id, { instruction }))) {
                setInstruction("");
                await load();
              }
            }}
          >
            Add
          </IpdButton>
        </Box>
      </IpdSectionCard>

      <IpdSectionCard
        title="Leaving recovery"
        subtitle="Closing the case is the last step of its lifecycle."
      >
        {recovery.departed_at ? (
          <FieldGrid columns={3}>
            <Field label="Destination" value={recovery.destination} />
            <Field label="Bed" value={recovery.destination_bed_label} />
            <Field label="Checked out by" value={recovery.checked_out_by_name} />
            {recovery.bed_action_error && (
              <Field label="Bed warning" value={recovery.bed_action_error} span={3} />
            )}
          </FieldGrid>
        ) : (
          <>
            <Button
              size="small"
              variant="outlined"
              disabled={recovery.ready_for_transfer || !recovery.dischargeable}
              onClick={async () => {
                if (report(await markReadyForTransfer(otCase.id))) refreshAll();
              }}
            >
              {recovery.ready_for_transfer ? "Ready for transfer" : "Mark ready for transfer"}
            </Button>

            <Box sx={{ mt: 2.5, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <IpdFormField label="Where is the patient going?">
                <OtSelect
                  value={checkout.destination}
                  onChange={(e) =>
                    setCheckout((c) => ({ ...c, destination: e.target.value }))
                  }
                  options={RECOVERY_DESTINATIONS}
                />
              </IpdFormField>
              {checkout.destination === "ICU" && (
                <IpdFormField label="ICU bed code">
                  <IpdTextField
                    fullWidth
                    value={checkout.destination_bed_code}
                    onChange={(e) =>
                      setCheckout((c) => ({ ...c, destination_bed_code: e.target.value }))
                    }
                  />
                </IpdFormField>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <IpdButton
                disabled={!recovery.ready_for_transfer}
                onClick={async () => {
                  if (report(await recoveryCheckout(otCase.id, checkout))) refreshAll();
                }}
              >
                Check out and close the case
              </IpdButton>
              {!recovery.ready_for_transfer && (
                <Typography sx={{ mt: 1, fontSize: 12, color: IPD_COLORS.textSecondary }}>
                  Mark the patient ready first — that needs a complete score of at least{" "}
                  {ALDRETE_DISCHARGEABLE_AT}.
                </Typography>
              )}
              {recovery.pain?.high && (
                <Typography sx={{ mt: 1, fontSize: 12.5, color: IPD_COLORS.warning.text }}>
                  Pain is {recovery.pain.latest} of {recovery.pain.max}. This does not stop
                  the check-out — hand over that the patient is in pain.
                </Typography>
              )}
            </Box>
          </>
        )}
      </IpdSectionCard>

      <VitalsDialog
        open={vitalsOpen}
        caseId={otCase.id}
        context="RECOVERY"
        onClose={() => setVitalsOpen(false)}
        onDone={() => {
          setVitalsOpen(false);
          load();
        }}
      />
    </Box>
  );
}
